/**
 * TanStack column defs for DocumentList — the `ColumnDef<ILibraryRow>[]` array
 * (cell renderers live in `documentListCells.tsx`).
 *
 * The hook (`useDocumentListTable`) composes this factory's output with
 * `useReactTable`. The factory is intentionally pure — pass options in, get
 * a column array out, no React hooks here.
 *
 * Column widths use the browser's default (auto) table layout: each column
 * sizes to its content. The checkbox is pinned to a fixed width, and the name
 * column is bounded (min/max) so it stays prominent and truncates long names.
 */
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { ColumnDef, RowData } from "@tanstack/react-table";
import { format, isToday, parseISO } from "date-fns";
import { BookCheck, ChevronRight } from "lucide-react";

declare module "@tanstack/react-table" {
    // Per-column className applied to the rendered `<TableHead>` and
    // `<TableCell>`. The select column uses this to override the default
    // `p-2` so its absolute-positioned cell wrapper can cover the full cell
    // area (see selectColumn meta below).
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface ColumnMeta<TData extends RowData, TValue> {
        cellClassName?: string;
    }
}

import {
    isForeignSharedSubject,
    isSharedRootFolder,
} from "../utils/folderTreeUtils";
import {
    getDocumentTypePill,
    getLibraryItemPill,
    type LibraryItemTypeKey,
} from "../utils/libraryItemTypes";
import { InlineStatusIndicatorOrIcon } from "@/components/shared/InlineStatusIndicatorOrIcon";

import { sharedKnowledgeBaseLabel } from "../utils/sharedKnowledgeBaseCopy";
import { ActionsForRow, ForeignSharedBadge } from "./documentListCells";
import type { ILibraryRow } from "./documentListColumns.types";

export type { ILibraryRow } from "./documentListColumns.types";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCreatedAt(value: string | null | undefined): string {
    if (!value) return "—";
    const date = typeof value === "string" ? parseISO(value) : new Date(value);
    return isToday(date) ? format(date, "h:mm a") : format(date, "MMM d, yyyy");
}

// ---------------------------------------------------------------------------
// Column factory
// ---------------------------------------------------------------------------

export interface DocumentListColumnsOptions {
    /** Callback fired after any destructive/mutating row-action menu item. */
    onDocumentAction: () => void;
    /**
     * Move-mode opts. When provided, the actions column renders a single
     * "navigate into folder" chevron in place of the per-type menus, and
     * non-folder rows render no action cell at all.
     */
    moveMode?: {
        onFolderNavigate: (folderId: number) => void;
        excludeFolderIds?: Set<number>;
    };
    /**
     * When true, the actions column is omitted entirely. Mirrors the legacy
     * `hideActionsColumn` derivation (`mode === "select" && hideRowActions`).
     */
    hideActions?: boolean;
    /**
     * Selection column behavior (Unit 2.IMPL.D — added for the shadcn body
     * rewire; MUI's DataGrid auto-injected this column when `checkboxSelection`
     * was true, but the shadcn `<Table>` needs an explicit column def):
     *   - `"multi"`  → prepend a select-all header + per-row checkboxes
     *   - `"single"` → no checkbox column (single-select binds row clicks)
     *   - omitted   → no checkbox column
     */
    selectionMode?: "multi" | "single";
    /**
     * Side-effect to fire BEFORE the select-all checkbox toggles the page
     * selection. Wires to `useLibrarySelection.markHeaderCheckboxClicked` so
     * a "deselect all" gesture clears off-page rows too (R6 cross-pagination
     * contract — see useLibrarySelection.test.ts).
     */
    onHeaderCheckboxClick?: () => void;
    /**
     * When true, the "Client" column is dropped. Used by client-scoped
     * surfaces (the workspace Library tab and the strictly client-scoped New
     * Scope picker) where every row belongs to the same client, so the column
     * is pure redundancy. The main document library leaves this false.
     */
    hideClientColumn?: boolean;
}

export function getDocumentListColumns({
    onDocumentAction,
    moveMode,
    hideActions = false,
    selectionMode,
    onHeaderCheckboxClick,
    hideClientColumn = false,
}: DocumentListColumnsOptions): ColumnDef<ILibraryRow>[] {
    const baseColumns: ColumnDef<ILibraryRow>[] = [
        {
            id: "name",
            accessorKey: "name",
            header: "Name",
            enableSorting: true,
            // Auto layout sizes this to its content; the bounds keep it
            // prominent (min) and truncate long names (max) so one long name
            // can't stretch the table.
            meta: { cellClassName: "min-w-[200px] max-w-[360px]" },
            cell: ({ row }) => {
                const { type, status, name, is_knowledge_base, _raw } =
                    row.original;
                const isFolder = type === "folder";
                const isForeign = isForeignSharedSubject(_raw);
                const isSharedRoot = isFolder && isSharedRootFolder(_raw);
                const isProjectedRoot = isForeign && isSharedRoot;
                const groupKnowledgeBase = sharedKnowledgeBaseLabel(
                    _raw.shared_group_name
                );
                const displayName = isProjectedRoot ? groupKnowledgeBase : name;

                const typeKey: LibraryItemTypeKey =
                    isFolder && is_knowledge_base && !_raw.parent_id
                        ? "knowledge_base"
                        : type;

                return (
                    <div className="flex min-w-0 items-center gap-2">
                        <InlineStatusIndicatorOrIcon
                            type={type}
                            status={status}
                            typeKey={typeKey}
                            fileType={_raw.file_type}
                        />
                        <TooltipProvider>
                            <Tooltip>
                                {/*
                                 * `tabIndex={0}` lets keyboard users focus the
                                 * name to reveal the tooltip when the text
                                 * is truncated. The row already carries its
                                 * own `tabIndex` (via DraggableRow) so this
                                 * adds a second stop within the row — but
                                 * only the truncated-name cell needs it.
                                 */}
                                <TooltipTrigger
                                    render={
                                        <span
                                            tabIndex={0}
                                            className={cn(
                                                "min-w-0 truncate rounded-sm text-sm",
                                                "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
                                                isFolder
                                                    ? "font-semibold"
                                                    : "font-medium"
                                            )}
                                        >
                                            {displayName}
                                        </span>
                                    }
                                />
                                <TooltipContent>
                                    {/*
                                     * `break-all` (word-break:
                                     * break-all) is what actually wraps
                                     * unbreakable filenames inside the
                                     * tooltip's `max-w-xs` cap.
                                     * `break-words` (overflow-wrap:
                                     * break-word) does NOT work here —
                                     * the tooltip Popup is `inline-flex`,
                                     * which gives its `<p>` child
                                     * `min-width: auto` (= min-content,
                                     * the longest unbreakable token).
                                     * `overflow-wrap: break-word` only
                                     * triggers when a break would
                                     * overflow, so the flex item just
                                     * stays at min-content width and
                                     * blows past the cap. `word-break:
                                     * break-all` forces breaks at any
                                     * character — including for
                                     * min-content calculation — so the
                                     * `<p>` can shrink and wrap.
                                     */}
                                    <p className="break-all">{displayName}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                        {isForeign ? (
                            <ForeignSharedBadge
                                isProjectedRoot={isProjectedRoot}
                                groupKnowledgeBase={groupKnowledgeBase}
                            />
                        ) : (
                            isSharedRoot && (
                                <Badge
                                    variant="secondary"
                                    className="min-w-0 shrink"
                                >
                                    <span className="truncate">
                                        Shared with group
                                    </span>
                                </Badge>
                            )
                        )}
                    </div>
                );
            },
        },
        {
            id: "type",
            header: "Type",
            enableSorting: false,
            cell: ({ row }) => {
                const { type, is_knowledge_base, _raw } = row.original;
                // A document names its file type ("PDF" / "Spreadsheet"); every
                // other row keeps its item-type pill.
                if (type === "document") {
                    return getDocumentTypePill(_raw.file_type);
                }
                const isFolder = type === "folder";
                const typeKey: LibraryItemTypeKey =
                    isFolder && is_knowledge_base && !_raw.parent_id
                        ? "knowledge_base"
                        : type;
                return getLibraryItemPill(typeKey);
            },
        },
        {
            id: "fields_count",
            accessorKey: "fields_count",
            header: "Fields",
            enableSorting: false,
            cell: ({ row }) => {
                if (row.original.type !== "document") return null;
                if (row.original.fields_count == null) {
                    return (
                        <span className="text-muted-foreground text-sm">—</span>
                    );
                }
                return (
                    <span className="text-sm">{row.original.fields_count}</span>
                );
            },
        },
        {
            id: "clause_checklist",
            accessorKey: "clause_checklist_name",
            header: "Checklist",
            enableSorting: true,
            cell: ({ row }) => {
                const { type, clause_checklist_name } = row.original;
                if (
                    (type !== "scope" && type !== "comparison") ||
                    !clause_checklist_name
                ) {
                    return (
                        <span className="text-muted-foreground text-sm">—</span>
                    );
                }
                return (
                    <Badge
                        className={cn(
                            "gap-1 border-transparent px-2 py-0.5",
                            // Brand indigo for "this analysis has a defined
                            // checklist" — a structure/authority signal.
                            "bg-primary/[0.08] text-primary"
                        )}
                    >
                        <BookCheck size={12} strokeWidth={1.8} aria-hidden />
                        <span className="truncate">
                            {clause_checklist_name}
                        </span>
                    </Badge>
                );
            },
        },
        {
            id: "client_name",
            accessorKey: "client_name",
            header: "Client",
            enableSorting: true,
            cell: ({ row }) => {
                const value = row.original.client_name;
                return (
                    <span
                        className={cn(
                            "truncate text-sm",
                            !value && "text-muted-foreground"
                        )}
                    >
                        {value || "—"}
                    </span>
                );
            },
        },
        {
            id: "metadata",
            header: "Tags",
            enableSorting: false,
            cell: ({ row }) => {
                if (row.original.type === "folder") {
                    return (
                        <span className="text-muted-foreground text-sm">—</span>
                    );
                }
                const { industry_name, carrier_name } = row.original;
                if (!industry_name && !carrier_name) {
                    return (
                        <span className="text-muted-foreground text-sm">—</span>
                    );
                }
                // Neutral muted pills — the text content (industry name,
                // carrier name) is the meaningful information, not the hue.
                // Two adjacent neutral pills read as "metadata" without
                // adding the rainbow-color anti-pattern.
                const tagClass =
                    "border-transparent bg-muted text-muted-foreground";
                return (
                    <div className="flex flex-wrap gap-1">
                        {industry_name && (
                            <Badge className={tagClass}>
                                <span className="truncate">
                                    {industry_name}
                                </span>
                            </Badge>
                        )}
                        {carrier_name && (
                            <Badge className={tagClass}>
                                <span className="truncate">{carrier_name}</span>
                            </Badge>
                        )}
                    </div>
                );
            },
        },
        {
            id: "created_at",
            accessorKey: "created_at",
            header: "Date Created",
            enableSorting: true,
            cell: ({ row }) => (
                <span className="text-muted-foreground text-sm">
                    {formatCreatedAt(row.original.created_at)}
                </span>
            ),
        },
    ];

    const selectColumn: ColumnDef<ILibraryRow> | null =
        selectionMode === "multi"
            ? {
                  id: "select",
                  enableSorting: false,
                  // `w-12` pins this column to a fixed, tight width so auto
                  // layout doesn't grow it. `p-0` removes the default TableCell
                  // `p-2` so the wrapper below fills the full cell area (no
                  // unclickable padding gutter); the wrapper supplies its own
                  // `px-3` to center the checkbox.
                  meta: { cellClassName: "w-12 p-0" },
                  header: ({ table }) => {
                      const pageRows = table.getRowModel().rows;
                      if (
                          pageRows.length > 0 &&
                          pageRows.every((row) =>
                              isForeignSharedSubject(row.original._raw)
                          )
                      ) {
                          return null;
                      }
                      const someSelected = table.getIsSomePageRowsSelected();
                      const allSelected = table.getIsAllPageRowsSelected();
                      const toggleAll = () => {
                          // Order matters: set the cross-page flag BEFORE
                          // toggling, so useLibrarySelection sees it in the
                          // resulting change emission.
                          onHeaderCheckboxClick?.();
                          table.toggleAllPageRowsSelected(!allSelected);
                      };
                      return (
                          <div
                              data-slot="select-cell"
                              onClick={(e) => {
                                  e.stopPropagation();
                                  toggleAll();
                              }}
                              className="flex h-full cursor-pointer items-center justify-center px-3"
                          >
                              <Checkbox
                                  checked={allSelected}
                                  indeterminate={someSelected && !allSelected}
                                  onCheckedChange={toggleAll}
                                  aria-label="Select all"
                                  // Wrapper consumes propagation; checkbox
                                  // can be pointer-inert without losing
                                  // keyboard activation (Space still fires
                                  // onCheckedChange via the inner Root).
                                  className="pointer-events-none"
                              />
                          </div>
                      );
                  },
                  cell: ({ row }) => {
                      if (isForeignSharedSubject(row.original._raw)) {
                          return null;
                      }
                      const canSelect = row.getCanSelect();
                      return (
                          <div
                              data-slot="select-cell"
                              onClick={(e) => {
                                  e.stopPropagation();
                                  if (!canSelect) return;
                                  row.toggleSelected(!row.getIsSelected());
                              }}
                              className={cn(
                                  "flex h-full items-center justify-center px-3",
                                  canSelect
                                      ? "cursor-pointer"
                                      : "cursor-not-allowed"
                              )}
                          >
                              <Checkbox
                                  checked={row.getIsSelected()}
                                  disabled={!canSelect}
                                  onCheckedChange={(checked) =>
                                      row.toggleSelected(!!checked)
                                  }
                                  aria-label="Select row"
                                  className="pointer-events-none"
                              />
                          </div>
                      );
                  },
              }
            : null;

    // Drop the Client column on client-scoped surfaces before the select /
    // actions composition, so neither the leading checkbox nor the trailing
    // actions column is affected. Hiding a column does not change the result
    // set, so no `resultSetKey` impact downstream.
    const visibleBaseColumns = hideClientColumn
        ? baseColumns.filter((c) => c.id !== "client_name")
        : baseColumns;

    const columnsWithSelect = selectColumn
        ? [selectColumn, ...visibleBaseColumns]
        : visibleBaseColumns;

    if (hideActions) return columnsWithSelect;

    const actionsColumn: ColumnDef<ILibraryRow> = {
        id: "actions",
        header: "",
        enableSorting: false,
        // Just the 3-dot menu button (or the move-mode chevron). No
        // hover-revealed hint here anymore (see
        // [[feedback-document-library-type-distinguishability]]), so the
        // column only needs room for the icon button itself.
        cell: ({ row }) => {
            if (moveMode) {
                if (row.original.type !== "folder") return null;
                const excluded = moveMode.excludeFolderIds?.has(
                    row.original._numericId
                );
                return (
                    <Button
                        variant="ghost"
                        size="icon"
                        disabled={excluded}
                        aria-label={`Navigate into ${row.original.name}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            moveMode.onFolderNavigate(row.original._numericId);
                        }}
                        // `before:` pseudo-element extends the click target
                        // to ~48px without growing the visible button —
                        // closes the WCAG 2.5.5 AAA gap (44px minimum).
                        className={cn(
                            "text-muted-foreground hover:text-primary",
                            "relative before:absolute before:-inset-2 before:content-['']"
                        )}
                    >
                        <ChevronRight size={18} aria-hidden />
                    </Button>
                );
            }

            return (
                <div className="flex items-center justify-end">
                    <ActionsForRow
                        row={row.original}
                        onAction={onDocumentAction}
                    />
                </div>
            );
        },
    };

    return [...columnsWithSelect, actionsColumn];
}
